import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import axios from 'axios';
import clsx from 'clsx';
import moment from 'moment';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, FormCheck, Image, InputGroup, Row } from 'react-bootstrap';
import { useDebounce } from '../../../../_metronic/helpers';
import useMultiLanguage from '../../../hook/useMultiLanguage';
import AutocompleteObjectV2 from '../../component/AutocompleteObjectV2';
import LabelRequired from '../../component/LabelRequired';
import TextField from '../../component/TextField';
import { formatDateDTO } from '../../utils/FormatUtils';
import CheckboxGroup from '../checkbox-group';
import DatePickerCustom from '../date-picker/DatePickerCustom';
import InputCellGroup from '../input-cell-group';
import InputOTP from '../input-otp';
import OCTTextValidator from '../text-validator';
import './confirmDialog.scss';
import './generateForm.scss';
import {
	COMPONENT_TYPE,
	DATATYPE_PROPERTIES,
	DATA_COMPONENT_PROPERTIES,
	generateSearchParams,
	replaceUrl,
	TAG,
} from './GenerateFormConfig';
import LightBox from '../image-lightbox';
import { toAbsoluteUrl } from '../../../../_metronic/helpers';
import { IDialogProps, ItemTypeProps } from './type';
import CanvasEditor from '../canvas-editor/CanvasEditor';
import Survey from '../survey';
import { localStorageItem } from '../../utils/LocalStorage';
import { KEY_LOCALSTORAGE } from '../../auth/core/_consts';
import CanvasImage from '../canvas-editor';

const GenerateFormHTMLNoValid = ({
	isView,
	modelID,
	onChange,
	values,
	errors,
	touched,
	handleChange = () => {},
	listFieldAuto,
	customComponent,
	customProps,
	setFieldValue,
	customSearchObject,
	readOnly,
	fieldConditionalConfig,
	itemEdit,
	getFormCustomConditionalConfig,
}: IDialogProps) => {
	const { lang } = useMultiLanguage();

	const fieldConditionalFnRef = useRef<Map<string, Function>>(new Map());
	const initialValueRef = useRef<Record<string, any>>({});

	const fileInputRef = useRef<Record<string, HTMLInputElement>>({});
	const [openLightBox, setOpenLightBox] = useState(false);
	const [lightBoxData, setLightBoxData] = useState<any[]>([]);

	const isDisabledAll = listFieldAuto?.tags?.includes(TAG.DISABLED);

	const toBase64 = (file: any) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const handleChangeFile = async (e: any, item: ItemTypeProps) => {
		let files = Array.from(e.target.files || []);
		let filesData = await Promise.all(
			files.map(async (file: any, index: number) => ({
				src: await toBase64(file),
				fileType: file.type,
				title: file.name,
				index,
			}))
		);
		setFieldValue?.(modelID, {
			...values?.[modelID],
			[e.target.name]: filesData,
		});
		await onChange?.(e.target.name, filesData);
	};

	const handleChangeCheck = (name: string, value: any, modelID: string, e: any) => {
		const checkNumberValue = Number(value).toString();
		setFieldValue?.(modelID, {
			...values?.[modelID],
			[name]: checkNumberValue,
		});
		onChange?.(name, checkNumberValue);
	};

	const handleChangeValueText = (
		name: string,
		value: any,
		modelID: string,
		dataComponent?: any
	) => {
		let valueConvert = value;

		if (dataComponent?.inputType === 'number') {
			valueConvert = Number(value);
		}

		if (dataComponent?.type === 'datetime') {
			valueConvert = formatDateDTO(value);
		}

		setFieldValue?.(modelID, {
			...values?.[modelID],
			[name]: valueConvert,
		});
		onChange?.(name, valueConvert);
	};

	const getValueFromTemplate = (keyStr: string, props: any, defaultValue: any = '') => {
		return keyStr.split('.').reduce((acc, currKey) => acc?.[currKey], props) ?? defaultValue;
	};

	const replaceTemplateString = (template: string, data: any, isPlainText: boolean = false) => {
		return template.replace(/\${(.*?)}/g, (_: any, key: any) => {
			const value = getValueFromTemplate(key, data);
			if (value === undefined) return '';
			// Nếu isPlainText = true, trả về string thuần không có dấu ngoặc kép
			return isPlainText ? String(value) : JSON.stringify(value);
		});
	};

	const replaceTemplateValues = (template: any, data: any): any => {
		if (typeof template === 'string') {
			return template.includes('${')
				? JSON.parse(replaceTemplateString(template, data))
				: template;
		}

		if (Array.isArray(template)) {
			return template.map((item) => replaceTemplateValues(item, data));
		}

		if (typeof template === 'object' && template !== null) {
			return Object.fromEntries(
				Object.entries(template).map(([key, value]) => [
					key,
					replaceTemplateValues(value, data),
				])
			);
		}

		return template;
	};

	const mapFieldsByConfig = (value: any, fieldMapping?: string) => {
		if (!fieldMapping || !value) return {};

		const mappedFields: Record<string, any> = {};

		try {
			// Parse string JSON thành object: '{"name": "personName", "code": "personCode"}'
			const mappingConfig: Record<string, string> = JSON.parse(fieldMapping);

			Object.entries(mappingConfig).forEach(([sourceField, targetKey]) => {
				// Hỗ trợ nested field sử dụng getValueFromTemplate
				const fieldValue = getValueFromTemplate(sourceField, value);
				if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
					mappedFields[targetKey] = fieldValue;
				}
			});
		} catch (error) {
			console.error('Error parsing fieldMapping string:', error);
			return {};
		}

		return mappedFields;
	};

	const handleChangeValueSelect = (value: any, modelID: string, dataComponent?: any) => {
		let type = 'select';
		const customField = replaceTemplateValues(dataComponent.properties, value);
		const isObjectType = dataComponent.properties.datatypeId === DATATYPE_PROPERTIES.OBJECT;

		const key = dataComponent?.key || '';

		// Map fields theo config nếu có
		const mappedFields = mapFieldsByConfig(value, dataComponent?.properties?.fieldMapping);

		if (isObjectType) {
			setFieldValue?.(modelID, {
				...values?.[modelID],
				...customField,
				...mappedFields,
				[key]: value,
			});
		} else {
			setFieldValue?.(modelID, {
				...values?.[modelID],
				[key]: value?.code || value?.id,
				[`${key}Id`]: value?.id,
				[`${key}Name`]: value?.name,
				[`${key}Code`]: value?.code,
				...customField,
				...mappedFields,
			});
		}

		onChange?.(dataComponent?.key, value, type, dataComponent?.properties);
	};

	const debouncedValues = useDebounce(values, 500);
	const [searchData, setSearchData] = useState(generateSearchParams(listFieldAuto));

	useEffect(() => {
		const newSearchData = { ...searchData };

		Object.keys(newSearchData).forEach((key) => {
			Object.keys(newSearchData[key]).forEach((keySearch) => {
				const newValue = values?.[modelID]?.[keySearch];
				if (newValue !== newSearchData[key][keySearch] && newValue !== undefined) {
					newSearchData[key][keySearch] = newValue;
				}
			});
		});

		setSearchData(newSearchData);
	}, [debouncedValues]);

	const handleGetDataByUrlData = (urlData?: string, searchObject?: any, dataComponent?: any) => {
		if (!urlData) return;
		const API_ENDPOINT =
			localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[(dataComponent?.selectValues ?? '').trim()] ||
			localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.['apiUrl'] ||
			process.env.REACT_APP_API_URL;

		const apiConfig = getFieldConfig(dataComponent, false)?.apiConfig;

		const url = `${API_ENDPOINT}/${urlData}`;

		const defaultMethod = urlData.includes('search') && !urlData.includes('?') ? 'post' : 'get';
		const funcApi =
			() => axios({
				method: defaultMethod,
				url,
				params: defaultMethod === 'get' ? searchObject : null,
				data: defaultMethod === 'post' ? searchObject : null,
				...apiConfig,
			});

		if (searchObject || dataComponent?.clearOnRefresh) {
			return funcApi();
		}
	};

	const generateHTMLByTag = (dataComponent: ItemTypeProps) => {
		const fieldValue = dataComponent?.customConditional
			? calculateFieldValue(dataComponent, isView || dataComponent.disabled)
			: dataComponent?.content ?? '';

		const componentMapper: Record<string, any> = {
			span: (
				<div>
					<LabelRequired
						className="min-w-100px"
						label={values?.[dataComponent?.key] || ''}
					/>
				</div>
			),
			p: (
				<div className={`spaces mt-1 ${dataComponent?.className}`}>
					<LabelRequired
						className={clsx('min-w-100px', dataComponent?.properties?.className)}
						isRequired={dataComponent?.className === 'isRequired'}
						label={fieldValue}
					/>
				</div>
			),
			label: (
				<label
					dangerouslySetInnerHTML={{
						__html: fieldValue,
					}}
					className={clsx(
						'text-lable-input max-content-width w-100 d-inline-block',
						dataComponent?.properties?.className,
						dataComponent?.className
					)}
				></label>
			),
		};

		const defaultComponent = <div className="hyperlink">{dataComponent?.label}</div>;

		return componentMapper[dataComponent?.tag ?? ''] || defaultComponent;
	};

	const getFieldConfig = (dataComponent: ItemTypeProps, isDisabled: boolean = false) => {
		try {
			const formValues = values?.[modelID] ?? {};
			const isExistField = fieldConditionalFnRef.current.has(dataComponent.key);

			if (!isExistField) return formValues[dataComponent.key] ?? '';

			const conditionalResult = fieldConditionalFnRef.current.get(
				dataComponent.key
			)!(
				formValues,
				formValues[dataComponent.key],
				isDisabled,
				dataComponent,
				moment,
				itemEdit,
				...Object.values(fieldConditionalConfig ?? {})
			);

			getFormCustomConditionalConfig?.({
				[dataComponent.key]: conditionalResult,
			});

			initialValueRef.current = {
				...initialValueRef.current,
				[dataComponent.key]:
					conditionalResult.initialValue ?? itemEdit?.[dataComponent.key] ?? null,
			};

			return conditionalResult;
		} catch (error) {
			console.error(error);
		}
	};

	const calculateFieldValue = (dataComponent: ItemTypeProps, isDisabled?: boolean) => {
		const noValue = (
			dataComponent?.type === COMPONENT_TYPE.TEXTFIELD ||
			dataComponent?.type === COMPONENT_TYPE.TEXTAREA
		) ? "" : null; 
		return (
			getFieldConfig(dataComponent, isDisabled)?.value ||
			values?.[modelID]?.[dataComponent?.key] ||
			noValue
		);
	};

	const renderFileComponent = (dataComponent: ItemTypeProps) => {
		switch (dataComponent?.properties?.mode) {
			case DATA_COMPONENT_PROPERTIES.MODE.CANVAS:
				return (
					<>
						{dataComponent.label && !dataComponent.hideLabel && (
							<Form.Label>{dataComponent?.label}</Form.Label>
						)}
						<CanvasImage 
							value={values?.[modelID]?.[dataComponent?.key]}
							title={dataComponent?.label ?? ''}
							onChange={(value) => {
								handleChangeValueText(
									dataComponent?.key,
									value,
									modelID,
									dataComponent
								);
							}}
							className={clsx("max-h-200px",dataComponent?.customClass)}
						/>
					</>
				);
			case DATA_COMPONENT_PROPERTIES.MODE.SINGLE_IMAGE:
				return (
					<div className="position-relative">
						<Form.Group controlId="formFile" className="mb-3">
							<Image
								src={
									values?.[modelID]?.[dataComponent?.key]?.[0]?.src ||
									toAbsoluteUrl('/media/images/image-placeholder.png')
								}
								alt="image"
								className={`spaces width-100 cursor-pointer border border-1 object-fit-cover ${
									dataComponent?.customClass ?? ''
								}`}
								onClick={() => fileInputRef.current?.[dataComponent?.key]?.click()}
							/>
							{values?.[modelID]?.[dataComponent?.key]?.[0]?.src && (
								<i
									className="bi bi-arrows-fullscreen position-absolute spaces d-inline-block p-10 bottom-0 right-0 fs-2 cursor-pointer bg-light"
									onClick={() => {
										setLightBoxData(values?.[modelID]?.[dataComponent?.key]);
										setOpenLightBox(true);
									}}
								></i>
							)}
							<Form.Control
								type="file"
								ref={(ref: HTMLInputElement) => {
									if (fileInputRef.current && dataComponent) {
										fileInputRef.current[dataComponent.key] = ref;
									}
								}}
								name={dataComponent.key}
								max={dataComponent?.fileMaxSize}
								min={dataComponent?.fileMinSize}
								accept={dataComponent?.filePattern}
								onChange={(e) => handleChangeFile(e, dataComponent)}
								disabled={isView || isDisabledAll || dataComponent?.disabled}
								className="d-none"
							/>
						</Form.Group>
					</div>
				);
			default:
				return (
					<Form.Group controlId="formFile" className="mb-3">
						{!dataComponent?.hideLabel && (
							<Form.Label>{dataComponent?.label}</Form.Label>
						)}
						<div
							className="d-inline cursor-pointer"
							onClick={(e) => {
								e.stopPropagation();
								fileInputRef.current?.[dataComponent?.key]?.click();
							}}
						>
							<InputGroup className="mb-3">
								<Form.Control
									type="text"
									readOnly
									disabled={isView || isDisabledAll || dataComponent?.disabled}
									placeholder="Chọn file"
									value={
										values?.[modelID]?.[dataComponent?.key]
											?.map((item: any) => item?.title)
											?.join('; ') || ''
									}
									className={clsx('customs-input', dataComponent?.customClass)}
								/>
								<Form.Control
									type="file"
									ref={(ref: HTMLInputElement) => {
										if (fileInputRef.current && dataComponent) {
											fileInputRef.current[dataComponent.key] = ref;
										}
									}}
									multiple={dataComponent?.multiple}
									name={dataComponent.key}
									max={dataComponent?.fileMaxSize}
									min={dataComponent?.fileMinSize}
									accept={dataComponent?.filePattern}
									onChange={(e) => handleChangeFile(e, dataComponent)}
									disabled={isView || isDisabledAll || dataComponent?.disabled}
									className="d-none"
								/>
								<Button
									variant="outline-secondary"
									id="button-addon2"
									onClick={(e) => {
										e.stopPropagation();
										setLightBoxData(values?.[modelID]?.[dataComponent?.key]);
										setOpenLightBox(true);
									}}
									className="btn-fill"
									disabled={!values?.[modelID]?.[dataComponent?.key]?.length}
								>
									<i className="bi bi-eye-fill pe-0 me-0"></i>
								</Button>
							</InputGroup>
						</div>
					</Form.Group>
				);
		}
	};

	const renderHTML = (dataForm: any, configKey?: string) => {
		if (dataForm?.components || dataForm?.[configKey || 'components']) {
			let newArray = dataForm?.components || dataForm?.[configKey || ''] || [];

			if (Object.prototype.toString.call(newArray) === '[object Object]') {
				newArray = Object.entries(newArray).map(([key, value]) => {
					return value;
				});
			}

			let filteredComponents = newArray?.filter(
				(item: ItemTypeProps) => item.type !== COMPONENT_TYPE.BUTTON
			);

			return filteredComponents?.map((dataComponent: ItemTypeProps) => {
				if (
					dataComponent.customConditional &&
					!fieldConditionalFnRef.current.has(dataComponent.key)
				) {
					const params = [
						'formValues',
						'fieldValue',
						'isDisabled',
						'dataComponent',
						'moment',
						'itemEdit',
						...Object.keys(fieldConditionalConfig ?? {}),
					];
					fieldConditionalFnRef.current.set(
						dataComponent.key,
						// eslint-disable-next-line no-new-func
						new Function(...params, dataComponent.customConditional)
					);
				}

				const isDisabled = isView || dataComponent.disabled;
				const fieldValue = calculateFieldValue(dataComponent, isDisabled);
				const label = <>
					{dataComponent?.hideLabel
						? null
						: <span>
							{dataComponent?.label}
							<span hidden={!dataComponent?.validate?.required} className="color-red"> (*)</span>
						</span>}
				</>;
				const isHidden = dataComponent?.hidden;
				if (isHidden) return <></>;

				switch (dataComponent?.type) {
					case COMPONENT_TYPE.EMAIL:
					case COMPONENT_TYPE.PASSWORD:
					case COMPONENT_TYPE.TEXTFIELD:
						return (
							<>
								<div className={`spaces pt-8 ${dataComponent?.overlay?.style}`}>
									<TextField
										label={label}
										name={dataComponent?.key}
										type={dataComponent.inputType}
										onChange={(e: any) =>
											handleChangeValueText(
												e.target.name,
												e.target.value,
												modelID
											)
										}
										value={fieldValue}
										touched={touched?.[modelID]?.[dataComponent?.key]}
										errors={errors?.[modelID]?.[dataComponent?.key]}
										placeholder={dataComponent.placeholder}
										disabled={isDisabled || isDisabledAll}
										className={dataComponent?.customClass}
										readOnly={readOnly}
										prefix={dataComponent.prefix}
										suffix={dataComponent.suffix}
										{...dataComponent.properties}
									/>
								</div>
							</>
						);
					case COMPONENT_TYPE.COLUMNS:
						return (
							<Row>
								{dataComponent?.columns &&
									dataComponent?.columns?.length > 0 &&
									dataComponent?.columns?.map(
										(dataColumn: ItemTypeProps, index: number) => {
											return (
												<Col
													key={index}
													lg={{
														span: dataColumn?.width,
														offset: dataColumn?.offset || 0,
													}}
													className={clsx(
														dataComponent?.properties?.columnClassName,
														dataComponent?.customClass
													)}
												>
													{renderHTML(dataColumn)}
												</Col>
											);
										}
									)}
							</Row>
						);
					case COMPONENT_TYPE.FILE:
						return renderFileComponent(dataComponent);
					case COMPONENT_TYPE.DAY:
						const isNgaySinh = dataComponent?.key === 'ngaySinh';
						return (
							<>
								{!dataComponent?.hideLabel && (
									<Form.Label>{dataComponent?.label}</Form.Label>
								)}
								<Row>
									<Col lg={isNgaySinh ? 3 : 4} className="spaces pr-0 mb-3 pt-8">
										<OCTTextValidator
											hideLabel={dataComponent?.hideLabel}
											lable={''}
											name={dataComponent.placeholder}
											type={'text'}
											onChange={handleChange}
											isRequired={dataComponent?.validate?.required}
											value={values?.[dataComponent?.key]}
											touched={touched?.[dataComponent?.key]}
											errors={errors?.[dataComponent?.key]}
											placeholder={''}
											disabled={isView}
										/>
									</Col>
									<Col
										lg={isNgaySinh ? 3 : 4}
										className="spaces mb-3 pl-0 pr-0 pt-8"
									>
										<OCTTextValidator
											hideLabel={dataComponent?.hideLabel}
											lable={''}
											name={dataComponent.placeholder}
											type={'text'}
											onChange={handleChange}
											isRequired={dataComponent?.validate?.required}
											value={values?.[dataComponent?.key]}
											touched={touched?.[dataComponent?.key]}
											errors={errors?.[dataComponent?.key]}
											placeholder={''}
											disabled={isView}
										/>
									</Col>
									<Col lg={isNgaySinh ? 3 : 4} className="spaces mb-3 pl-0 pt-8">
										<OCTTextValidator
											hideLabel={dataComponent?.hideLabel}
											lable={''}
											name={dataComponent.placeholder}
											type={'text'}
											onChange={handleChange}
											isRequired={dataComponent?.validate?.required}
											value={values?.[dataComponent?.key]}
											touched={touched?.[dataComponent?.key]}
											errors={errors?.[dataComponent?.key]}
											placeholder={''}
											disabled={isView}
										/>
									</Col>
									{isNgaySinh && (
										<>
											<Col lg={1} className="spaces mb-3 pl-0 pt-8">
												<div className="spaces d-flex flex-center fw-bold">
													-
												</div>
											</Col>

											<Col lg={2} className="spaces mb-3 pl-0 pt-8">
												<OCTTextValidator
													hideLabel={dataComponent?.hideLabel}
													lable={''}
													name={dataComponent?.key}
													type={'text'}
													onChange={handleChange}
													isRequired={dataComponent?.validate?.required}
													value={values?.[dataComponent?.key]}
													touched={touched?.[dataComponent?.key]}
													errors={errors?.[dataComponent?.key]}
													placeholder={''}
													disabled={isView}
												/>
											</Col>
										</>
									)}
								</Row>
							</>
						);
					case COMPONENT_TYPE.TIME:
						return (
							<>
								{!dataComponent?.hideLabel && (
									<Form.Label>{dataComponent?.label}</Form.Label>
								)}
								<Form.Control
									type="time"
									placeholder="Time"
									onChange={handleChange}
									value={values?.[dataComponent?.key]}
								/>
							</>
						);
					case COMPONENT_TYPE.DATETIME:
						return (
							<div className={`spaces pt-8 ${dataComponent?.overlay?.style}`}>
								<DatePickerCustom
									name={dataComponent?.key}
									value={fieldValue}
									setDateValue={(date) => {
										handleChangeValueText(
											dataComponent?.key,
											date,
											modelID,
											dataComponent
										);
									}}
									touched={touched?.[modelID]?.[dataComponent?.key]}
									errors={errors?.[modelID]?.[dataComponent?.key]}
									placeholder={dataComponent.placeholder}
									disabled={isDisabled || isDisabledAll}
									label={dataComponent.hideLabel ? '' : dataComponent?.label}
									required={dataComponent?.validate?.required}
									readOnly={readOnly}
									className={dataComponent?.customClass}
									{...dataComponent.properties}
								/>
							</div>
						);
					case COMPONENT_TYPE.NUMBER:
					case COMPONENT_TYPE.PHONE:
						return (
							<div className={`spaces pt-8 ${dataComponent?.overlay?.style}`}>
								<TextField
									hideLabel={dataComponent?.hideLabel}
									label={dataComponent?.label}
									name={dataComponent?.key}
									type={'number'}
									onChange={(e: any) =>
										handleChangeValueText(
											e.target.name,
											e.target.value,
											modelID,
											dataComponent
										)
									}
									value={fieldValue}
									touched={touched?.[modelID]?.[dataComponent?.key]}
									errors={errors?.[modelID]?.[dataComponent?.key]}
									placeholder={dataComponent.placeholder}
									disabled={isDisabled || isDisabledAll}
									{...dataComponent.properties}
								/>
							</div>
						);
					case COMPONENT_TYPE.TEXTAREA:
						return (
							<TextField
								name={dataComponent?.key}
								className={clsx(
									'd-block resize-none m-0',
									dataComponent?.customClass
								)}
								labelClassName="lh-lg"
								onChange={(e: any) =>
									handleChangeValueText(e.target.name, e.target.value, modelID)
								}
								as="textarea"
								rows={dataComponent?.rows}
								disabled={isDisabled || isDisabledAll}
								value={fieldValue}
								touched={touched?.[modelID]?.[dataComponent?.key]}
								errors={errors?.[modelID]?.[dataComponent?.key]}
								label={label}
								containerClassName="spaces pt-10"
								readOnly={readOnly}
								placeholder={dataComponent?.placeholder}
								{...dataComponent.properties}
							/>
						);
					case COMPONENT_TYPE.CHECKBOX:
						return (
							<Form.Group
								className={clsx(
									'spaces pt-10 form-check-label-black',
									dataComponent?.customClass
								)}
								controlId={dataComponent?.key}
							>
								<FormCheck
									name={dataComponent.key}
									type="checkbox"
									label={dataComponent?.hideLabel ? '' : dataComponent?.label}
									className={clsx(
										'd-flex align-items-center spaces gap-7',
										dataComponent.properties?.className
									)}
									checked={String(fieldValue) === '1'}
									onChange={(e: any) =>
										handleChangeCheck(
											e.target.name,
											e.target.checked,
											modelID,
											e
										)
									}
									disabled={isDisabled || isDisabledAll || readOnly}
									{...dataComponent.properties}
								/>
							</Form.Group>
						);
					case COMPONENT_TYPE.SELECTBOXES:
						return (
							<CheckboxGroup
								data={dataComponent?.values}
								name={dataComponent?.key}
								label={dataComponent?.hideLabel ? '' : dataComponent?.label}
								disabled={isDisabled || isDisabledAll}
								inline={dataComponent?.inline}
								containerClassName={clsx(
									'spaces mt-15',
									dataComponent.properties?.containerClassName,
									dataComponent.customClass,
								)}
								mode={dataComponent.properties?.mode ?? 'single'}
								value={fieldValue}
								onChange={(value: any) => {
									handleChangeValueText(
										dataComponent?.key,
										value,
										modelID,
										dataComponent
									);
								}}
							/>
						);
					case COMPONENT_TYPE.RADIO:
						return (
							<div className={dataComponent?.customClass}>
								{!dataComponent?.hideLabel && (
									<Form.Label className={clsx(
										'spaces text-lable-input max-content-width mb-0 min-w-fit-content',
										dataComponent.properties?.labelClassName
									)}>{dataComponent?.label}</Form.Label>
								)}
								<Form.Group
									className="d-flex flex-grow-1 spaces pt-8"
									controlId="formBasicCheckbox"
								>
									{dataComponent?.values?.map((dataItem: any) => (
										<Form.Check
											inline
											className={dataComponent?.properties?.itemClassName}
											type="radio"
											label={dataItem?.label}
											value={dataItem.value}
											name={dataComponent?.key}
											checked={fieldValue === dataItem.value}
											onChange={() => {
												if (onChange) {
													onChange(dataComponent?.key, dataItem.value);
												} else {
													setFieldValue?.(modelID, {
														...values?.[modelID],
														[dataComponent?.key]: dataItem.value,
													});
												}
											}}
											disabled={isDisabled || isDisabledAll}
										/>
									))}
								</Form.Group>
							</div>
						);
					case COMPONENT_TYPE.SELECT: {
						const options = dataComponent.data?.values;
						let newOptions = [];

						const hasEmptyValue = options.some(
							(item: { value: string; label: string }) =>
								item.label === '' || item.value === ''
						);

						if (!hasEmptyValue) {
							newOptions = options.map((item: { value: string; label: string }) => ({
								name: item.label,
								code: item.value,
							}));
						}

						// Hàm generate label từ optionLabelFormat
						const getFormattedLabel = (option: any) => {
							// Nếu có optionLabelFormat, sử dụng replaceTemplateString để format
							if (dataComponent?.properties?.optionLabelFormat) {
								try {
									// Sử dụng replaceTemplateString với isPlainText = true để không stringify
									const formattedString = replaceTemplateString(
										dataComponent.properties.optionLabelFormat,
										option,
										true
									);
									return formattedString;
								} catch (error) {
									console.error('Error formatting label:', error);
									// Fallback về displayField hoặc name
									return dataComponent?.properties?.displayField
										? option[dataComponent?.properties?.displayField]
										: option.name;
								}
							}
							// Fallback về displayField hoặc name nếu không có optionLabelFormat
							return dataComponent?.properties?.displayField
								? option[dataComponent?.properties?.displayField]
								: option.name;
						};

						return (
							<div className={dataComponent?.overlay?.style}>
								<AutocompleteObjectV2
									options={newOptions}
									isSearchDefauilt
									name={dataComponent?.key}
									onChange={(e: any) =>
										handleChangeValueSelect(e, modelID, dataComponent)
									}
									searchFunction={
										dataComponent?.data?.url
											? () =>
													handleGetDataByUrlData(
														replaceUrl(dataComponent?.data?.url || ''),
														{
															...customSearchObject?.[
																dataComponent?.key
															],
															...searchData?.[dataComponent?.key],
															...getFieldConfig(dataComponent, isDisabled)?.apiConfig
														},
														dataComponent
													)
											: null
									}
									value={fieldValue}
									touched={touched?.[modelID]?.[dataComponent?.key]}
									errors={errors?.[modelID]?.[dataComponent?.key]}
									placeholder={dataComponent?.placeholder || ''}
									searchObject={{
										...customSearchObject?.[dataComponent?.key],
										...searchData?.[dataComponent?.key],
										...getFieldConfig(dataComponent, isDisabled)?.apiConfig
									}}
									className="autocomplete-custom-renderform spaces radius-3 width-100 h-29"
									isDisabled={isView}
									label={label}
									isReadOnly={readOnly}
									containerClassName={clsx(
										"spaces mt-8",
										dataComponent?.customClass
									)}
									getOptionLabel={getFormattedLabel}
									getOptionValue={(option) =>
										dataComponent?.properties?.valueField
											? option[dataComponent?.properties?.valueField]
											: option.code
									}
									isMulti={dataComponent.multiple}
									disabled={isDisabled || isDisabledAll}
									{...dataComponent.properties}
								/>
							</div>
						);
					}
					case COMPONENT_TYPE.BUTTON:
						return (
							<>
								<Button>
									{lang(`BTN.${dataComponent?.label?.toUpperCase()}`)}
								</Button>
							</>
						);
					case COMPONENT_TYPE.CONTENT:
						return (
							<div className="spaces pt-10 ">
								<CKEditor
									editor={ClassicEditor}
									disabled={isView}
									onChange={(event: any, editor: any) => {
										onChange(dataComponent?.key, editor.getData());
									}}
									data={values?.[dataComponent?.key] || ''}
									// data={values?.[dataComponent?.key] || ""}
								/>
							</div>
						);
					case COMPONENT_TYPE.HTML:
						return (
							<div
								className={clsx(
									'spaces pt-10',
									dataComponent?.properties?.containerClassName
								)}
							>
								{generateHTMLByTag(dataComponent)}
							</div>
						);
					case COMPONENT_TYPE.OTP:
						return (
							<div
								className={clsx(
									`spaces pt-8`,
									dataComponent?.overlay?.style,
									dataComponent?.className
								)}
							>
								<InputOTP
									length={dataComponent?.length || 2}
									value={fieldValue}
									label={dataComponent.hideLabel ? '' : dataComponent?.label}
									onChange={(value: string) => {
										handleChangeValueText(dataComponent?.key, value, modelID);
									}}
									disabled={isDisabled || isDisabledAll}
									readOnly={readOnly}
								/>
							</div>
						);
					case COMPONENT_TYPE.INPUT_CELL_GROUP:
						return (
							<div
								className={clsx(
									`spaces pt-8`,
									dataComponent?.overlay?.style,
									dataComponent?.className
								)}
							>
								<InputCellGroup
									cells={dataComponent?.cells || []}
									value={values?.[modelID]}
									label={dataComponent.hideLabel ? '' : dataComponent?.label}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										handleChangeValueText(
											e.target.name,
											e.target.value,
											modelID
										);
									}}
									disabled={isDisabled || isDisabledAll}
									readOnly={readOnly}
									cellFlex={dataComponent.cellFlex}
									inputClassName={dataComponent.inputClassName}
									cellWrapperClassName={dataComponent.cellWrapperClassName}
								/>
							</div>
						);
					case COMPONENT_TYPE.CUSTOM:
						let searchDataCustom: any = null;
						if (dataComponent.searchData) {
							searchDataCustom = replaceTemplateValues(
								dataComponent.searchData,
								values[modelID]
							);
						}
						return customComponent ? (
							customComponent({
								data: {
									componentName: dataComponent.key,
									values: values?.[modelID] || '',
									isView,
									searchData: searchDataCustom,
									readOnly,
									properties: dataComponent,
									label: dataComponent.hideLabel
										? undefined
										: dataComponent.label,
									modelID,
									disabled: isDisabled || isDisabledAll,
									customProps,
								},
							})
						) : (
							<></>
						);
					case COMPONENT_TYPE.SURVEY: {
						const answers: any = dataComponent.values ?? [];
						const questions: any = dataComponent.questions.map((question: any) => ({
							...question,
							field: question.value,
							value: values?.[modelID]?.[question.value],
						}));

						return (
							<Survey
								label={dataComponent?.hideLabel ? undefined : dataComponent.label}
								data={questions}
								values={answers}
								disabled={isDisabled}
								onChange={(field, value) => {
									if (onChange) {
										onChange(field, value);
									} else {
										setFieldValue?.(modelID, {
											...values?.[modelID],
											[field]: value,
										});
									}
								}}
							/>
						);
					}
					default:
						return <></>;
				}
			});
		} else {
			return <></>;
		}
	};

	return (
		<Form>
			{renderHTML(listFieldAuto)}
			{openLightBox && (
				<LightBox
					open={openLightBox}
					onClose={() => setOpenLightBox(false)}
					images={lightBoxData || []}
					indexActive={0}
				/>
			)}
		</Form>
	);
};

export default GenerateFormHTMLNoValid;
